import { Observable, Subject as RxjsSubject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationError } from '../../../_errors/application-error';
import { ISubject, Subject, SubjectNew, SubjectUpdate } from '../../../_objects/subject';
import { SubjectModelListItem } from '../../../_objects/subject-model';
import { ConfigurationItem } from '../../../_objects/tenant-config';
import { UtilsService } from '../../../_services/_utils.service';
import { CommonSnackBarService } from '../../../_services/common-snack-bar.service';
import { SubjectModelService } from '../../../_services/http-api/subject-model.service';
import { SubjectService } from '../../../_services/http-api/subject.service';
import { TenantConfigService } from '../../../_services/http-api/tenant-config.service';
import { LangUpdateService } from '../../../_services/lang-update.service';
import { SpinnerService } from '../../../_services/spinner.service';
import { CustomValidators } from '../../../_shared/custom-validator';
import { REGULAR_EXPRESSION, TenantConfig } from '../../../app.constants';
import { CommonErrorMessageDialogComponent } from '../../../common-dialog/common-error-message-dialog.component';
import { CommonSaveConfirmDialogComponent } from '../../../common-dialog/common-save-confirm-dialog.component';

type SelectedTabType =  null | 'SN' | 'Attributes';

@Component({
    selector: 'app-settings-subject-edit-dialog',
    templateUrl: './settings-subject-edit-dialog.component.html',
    styleUrls: ['./settings-subject-edit-dialog.component.scss'],
    standalone: false
})
export class SettingsSubjectEditDialogComponent implements OnInit, AfterViewInit, OnDestroy {

  readonly serialMaxLength = 100;
  readonly ownerKey = TenantConfig.SubjectAttributeOwner.KEY;
  readonly countryKey = TenantConfig.SubjectAttributeCountry.KEY;
  readonly stateKey = TenantConfig.SubjectAttributeState.KEY;
  readonly districtKey = TenantConfig.SubjectAttributeDistrict.KEY;
  readonly siteKey = TenantConfig.SubjectAttributeSite.KEY;
  readonly projectKey = TenantConfig.SubjectAttributeProject.KEY;

  visibleAttrExists = false;

  selectedTab: SelectedTabType = null;

  serialNumberLabel = TenantConfig.DefaultConfigurationValues.LabelOfSerialNumber;

  subject: Subject;

  subjectModels: SubjectModelListItem[] = [];

  subjectModelForm = new FormControl<string>(null, [CustomValidators.requiredTrimmed()]);

  tenantConfigs: ConfigurationItem[] = [];

  regularExpression = this.utilsService.CombiningRegExp([
    '^',
    REGULAR_EXPRESSION.RESERVED_WORDS_PATTERN,
    '[',
    REGULAR_EXPRESSION.HALF_ALPHANUMERIC_PATTERN,
    REGULAR_EXPRESSION.KANJI_PATTERN,
    REGULAR_EXPRESSION.SYMBOL_PATTERN,
    ']+$'
  ]);

  inspectionTitleRegex = this.utilsService.CombiningRegExp([
    '^',
    REGULAR_EXPRESSION.RESERVED_WORDS_PATTERN,
    '[',
    REGULAR_EXPRESSION.HALF_ALPHANUMERIC_PATTERN,
    REGULAR_EXPRESSION.SYMBOL_PATTERN,
    ']+$'
  ]);

  serialNumberForm = new FormControl<string>('', [
    CustomValidators.requiredTrimmed(),
    Validators.maxLength(this.serialMaxLength),
    Validators.pattern(this.regularExpression)
  ]);
  ownerForm: FormControl<string>;
  countryForm: FormControl<string>;
  stateForm: FormControl<string>;
  districtForm: FormControl<string>;
  siteForm: FormControl<string>;
  projectForm: FormControl<string>;
  attributesForm: FormGroup<{
    owner: FormControl<string>;
    country: FormControl<string>;
    state: FormControl<string>;
    district: FormControl<string>;
    site: FormControl<string>;
    project: FormControl<string>;
  }>;

  // ラベル
  ownerLabel = TenantConfig.DefaultConfigurationValues.LabelOfOwner;
  countryLabel = TenantConfig.DefaultConfigurationValues.LabelOfCountry;
  stateLabel = TenantConfig.DefaultConfigurationValues.LabelOfState;
  districtLabel = TenantConfig.DefaultConfigurationValues.LabelOfRegion;
  siteLabel = TenantConfig.DefaultConfigurationValues.LabelOfSite;
  projectLabel = TenantConfig.DefaultConfigurationValues.LabelOfProject;

  readonly regularExpressionStr = 'a-z A-Z 0-9 !#$%&\'()+\\-;=@\\[\\]^`{}~_,.';
  private onDestroy$ = new RxjsSubject();

  @ViewChild(MatSlideToggle) toggle: MatSlideToggle;

  get toggleText(): string {
    return this.toggle && this.toggle.checked
      ? this.translate.instant('subjectEditDialog.enabled')
      : this.translate.instant('subjectEditDialog.disabled');
  }

  update: boolean;
  hasInspections: boolean;
  attrChanged: boolean;
  modelSelectable: boolean;
  isEditAttrOnly: boolean;
  // 二度押し防止
  isProcess = false;

  get isSubjectAttrs() {
    return this.ownerForm
      || this.countryForm
      || this.stateForm
      || this.districtForm
      || this.siteForm
      || this.projectForm;
  }

  get isContainKanjiForSerial() {
    return this.serialNumberForm.valid && !this.inspectionTitleRegex.test(this.serialNumberForm.value);
  }

  constructor(
    public dialogRef: MatDialogRef<SettingsSubjectEditDialogComponent, Subject>,
    @Inject(MAT_DIALOG_DATA) public data: { subject?: Subject, subjectModelId?: string, tenantConfigs: ConfigurationItem[], isEditAttrOnly?: boolean },
    private snackBar: CommonSnackBarService,
    private subjectService: SubjectService,
    private subjectModelService: SubjectModelService,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private oscSpinnerService: SpinnerService,
    private tenantConfigService: TenantConfigService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private utilsService: UtilsService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
  }

  ngOnInit() {
    this.subject = this.data.subject;
    this.update = this.subject != null;
    this.hasInspections = !!this.subject?.hasInspections;
    this.isEditAttrOnly = !!this.data.isEditAttrOnly;

    if (this.isEditAttrOnly) this.selectedTab = 'Attributes';
    else if (this.update) this.selectedTab = 'SN';

    if (this.data.subjectModelId) {
      this.modelSelectable = false;
      this.subjectModelForm.setValue(this.data.subjectModelId.trim());
    } else {
      this.modelSelectable = true;
      this.subjectModelService.getSubjectModels().subscribe(
        models => {
          this.subjectModels = models.sort((subjectModel1, subjectModel2) => {
            if (subjectModel1.name < subjectModel2.name) { return -1; }
            if (subjectModel1.name > subjectModel2.name) { return 1; }
            return 0;
          });
        });
    }

    this.tenantConfigs = this.tenantConfigService.tenantConfig;
    this.setSubjectAttrForms();
    this.setConfig();
  }

  ngAfterViewInit(): void {
    if (this.update) {
      const subject = this.data.subject;
      this.serialNumberForm.setValue(subject.serialNo.trim());
      this.toggle.checked = subject.isValid;
    } else {
      this.toggle.checked = true;
    }
    this.cd.detectChanges();
  }

  ngOnDestroy() {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  isDisabledSubmit(): boolean {
    return this.isProcess
      || this.serialNumberForm.invalid
      || this.subjectModelForm.invalid
      || (this.ownerForm && this.ownerForm.invalid)
      || (this.countryForm && this.countryForm.invalid)
      || (this.stateForm && this.stateForm.invalid)
      || (this.districtForm && this.districtForm.invalid)
      || (this.siteForm && this.siteForm.invalid)
      || (this.projectForm && this.projectForm.invalid);
  }

  loadSubject() {
    this.attrChanged = false;
    this.subjectService.getSubject(this.subject.id).subscribe(s => {
      this.subject = s;
      this.hasInspections = !!s.hasInspections;
    });
  }

  submit(keepDialog?: boolean): void {
    if (!this.serialNumberForm.value) {
      throw new ApplicationError('Serial number is invalid.');
    }

    let observable: Observable<Subject>;
    if (this.update) {
      observable = this.updateSubject(this.subject, this.serialNumberForm.value.trim(), this.toggle.checked);
    } else {
      observable = this.insertSubject(this.subjectModelForm.value, this.serialNumberForm.value.trim());
    }
    this.oscSpinnerService.show(true);
    this.isProcess = true;
    observable.subscribe(
      value => {
        this.snackBar.open(this.translate.instant('subjectEditDialog.submitSuccessfully'));
        keepDialog ? this.loadSubject() : this.dialogRef.close(value);
        this.oscSpinnerService.hide(true);
        this.isProcess = false;
      },
      (error: HttpErrorResponse) => {
        this.oscSpinnerService.hide(true);
        this.isProcess = false;
        if (error.status === 409) {
          this.openErrorMessageDialog();
        } else {
          console.log(error);
          this.snackBar.open(this.translate.instant('subjectEditDialog.submitFailed'));
        }
      }
    );
  }

  insertSubject(subjectModelId: string, newSerialNumber: string): Observable<Subject> {
    if (!subjectModelId) {
      throw new ApplicationError('Inspection Template is invalid.');
    }
    if (!newSerialNumber) {
      throw new ApplicationError('Serial number is invalid.');
    }

    const subjectUpdate: SubjectNew = this.createSubjectNew(subjectModelId, newSerialNumber);

    return this.subjectService.addSubject(subjectUpdate);
  }

  createSubjectNew(newSubjectModelId: string, newSerialNumber: string): SubjectNew {
    if (!newSubjectModelId) {
      throw new ApplicationError('Inspection Template is invalid.');
    }
    if (!newSerialNumber) {
      throw new ApplicationError('Serial number is invalid.');
    }
    let subjectNew = new SubjectNew();
    subjectNew.serialNo = newSerialNumber.trim();
    subjectNew.schemaVersion = '1';
    subjectNew.subjectModelId = newSubjectModelId;

    subjectNew = this.setSubjectAttrFormValue(subjectNew) as SubjectNew;

    return subjectNew;
  }

  updateSubject(subject: Subject, newSerialNumber: string, newEnabled: boolean): Observable<Subject> {
    if (!subject || !subject.id || !subject.subjectModelId) {
      throw new ApplicationError('Serial Number is invalid.');
    }
    if (!newSerialNumber) {
      throw new ApplicationError('Serial number is invalid.');
    }

    const subjectUpdate: SubjectUpdate = this.createSubjectUpdate(subject, newSerialNumber, newEnabled);

    return this.subjectService.updateSubject(subjectUpdate);
  }

  createSubjectUpdate(subject: Subject, newSerialNumber: string, newEnabled: boolean): SubjectUpdate {
    if (!subject || !subject.id || !subject.subjectModelId) {
      throw new ApplicationError('Serial Number is invalid.');
    }
    if (!newSerialNumber) {
      throw new ApplicationError('Serial number is invalid.');
    }

    let subjectUpdate = new SubjectUpdate();
    subjectUpdate.serialNo = newSerialNumber.trim();
    subjectUpdate.isValid = newEnabled;
    subjectUpdate.id = subject.id;
    subjectUpdate.schemaVersion = subject.schemaVersion;
    subjectUpdate.subjectModelId = subject.subjectModelId;

    subjectUpdate = this.setSubjectAttrFormValue(subjectUpdate) as SubjectUpdate;

    return subjectUpdate;
  }

  openErrorMessageDialog(): void {
    const message1 = this.translate.instant('subjectEditDialog.openErrorMessageDialogFirstMessage1');
    const message2 = this.translate.instant('subjectEditDialog.openErrorMessageDialogFirstMessage2');
    this.dialog.open(CommonErrorMessageDialogComponent, {
      width: '480px',
      height: '400px',
      data: {
        firstMessage: message1 + this.serialNumberLabel + message2,
        secondMessage: this.translate.instant('subjectEditDialog.openErrorMessageDialogSecondMessage'),
      }
    });
  }

  getRequiredErrorText(target: string): string {
    const message = this.translate.instant('subjectEditDialog.requiredErrorText');
    return target + message;
  }

  getValidErrorText(target: string): string {
    const message = this.translate.instant('subjectEditDialog.validErrorText');
    return message + target + '.';
  }

  setSubjectAttrForms() {
    const subject = this.subject;
    const baseValidators = [
      Validators.maxLength(this.serialMaxLength),
      Validators.pattern(this.regularExpression)
    ];

    // Owner Init
    const ownerConfig = this.tenantConfigs?.find(c => c.key === TenantConfig.SubjectAttributeOwner.KEY);
    const ownerValidator = ownerConfig.value === TenantConfig.DISPLAY_AND_REQUIRED
      ? baseValidators.concat([CustomValidators.requiredTrimmed()])
      : baseValidators;
    const ownerValue = this.update
      ? subject.owner?.trim()
      : null;
    this.ownerForm = new FormControl<string>(ownerValue, ownerValidator);

    // Country Init
    const countryConfig = this.tenantConfigs?.find(c => c.key === TenantConfig.SubjectAttributeCountry.KEY);
    const countryValidator = countryConfig.value === TenantConfig.DISPLAY_AND_REQUIRED
      ? baseValidators.concat([CustomValidators.requiredTrimmed()])
      : baseValidators;
    const countryValue = this.update
      ? subject.country?.trim()
      : null;

    this.countryForm = new FormControl<string>(countryValue, countryValidator);

    // State Init
    const stateConfig = this.tenantConfigs?.find(c => c.key === TenantConfig.SubjectAttributeState.KEY);
    const stateValidator = stateConfig.value === TenantConfig.DISPLAY_AND_REQUIRED
      ? baseValidators.concat([CustomValidators.requiredTrimmed()])
      : baseValidators;
    const stateValue = this.update
      ? subject.state?.trim()
      : null;

    this.stateForm = new FormControl<string>(stateValue, stateValidator);

    // District Init
    const districtConfig = this.tenantConfigs?.find(c => c.key === TenantConfig.SubjectAttributeDistrict.KEY);
    const districtValidator = districtConfig.value === TenantConfig.DISPLAY_AND_REQUIRED
      ? baseValidators.concat([CustomValidators.requiredTrimmed()])
      : baseValidators;
    const districtValue = this.update
      ? subject.district?.trim()
      : null;

    this.districtForm = new FormControl<string>(districtValue, districtValidator);

    // Site Init
    const siteConfig = this.tenantConfigs?.find(c => c.key === TenantConfig.SubjectAttributeSite.KEY);
    const siteValidator = siteConfig.value === TenantConfig.DISPLAY_AND_REQUIRED
      ? baseValidators.concat([CustomValidators.requiredTrimmed()])
      : baseValidators;
    const siteValue = this.update
      ? subject.site?.trim()
      : null;

    this.siteForm = new FormControl<string>(siteValue, siteValidator);

    // Project Init
    const projectConfig = this.tenantConfigs?.find(c => c.key === TenantConfig.SubjectAttributeProject.KEY);
    const projectValidator = projectConfig.value === TenantConfig.DISPLAY_AND_REQUIRED
      ? baseValidators.concat([CustomValidators.requiredTrimmed()])
      : baseValidators;
    const projectValue = this.update
      ? subject.project?.trim()
      : null;

    this.projectForm = new FormControl<string>(projectValue, projectValidator);

    this.attributesForm = new FormGroup({
      owner: this.ownerForm,
      country: this.countryForm,
      state: this.stateForm,
      district: this.districtForm,
      site: this.siteForm,
      project: this.projectForm
    });
    this.attributesForm.valueChanges.subscribe(val => {
      // 1つでも変更あるか監視
      if (
        subject?.owner != val.owner ||
        subject?.country != val.country ||
        subject?.state != val.state ||
        subject?.district != val.district ||
        subject?.site != val.site ||
        subject?.project != val.project
      ) {
        this.attrChanged = true;
      } else {
        this.attrChanged = false;
      }
    });
  }

  setConfig() {
    this.visibleAttrExists = this.tenantConfigService.visibleAttrExists;

    if (this.tenantConfigs) {
      this.ownerLabel = this.tenantConfigs.find(_ => _.key === TenantConfig.LabelOfOwner.KEY).value as string;
      this.countryLabel = this.tenantConfigs.find(_ => _.key === TenantConfig.LabelOfCountry.KEY).value as string;
      this.stateLabel = this.tenantConfigs.find(_ => _.key === TenantConfig.LabelOfState.KEY).value as string;
      this.districtLabel = this.tenantConfigs.find(_ => _.key === TenantConfig.LabelOfRegion.KEY).value as string;
      this.siteLabel = this.tenantConfigs.find(c => c.key === TenantConfig.LabelOfSite.KEY).value as string;
      this.projectLabel = this.tenantConfigs.find(c => c.key === TenantConfig.LabelOfProject.KEY).value as string;
      const snLabelConfig = this.tenantConfigs.find(_ => _.key === TenantConfig.LabelOfSerialNumber.KEY);
      if (snLabelConfig) {
        this.serialNumberLabel = snLabelConfig.value as string;
      }
    }
  }

  isRequired(attrKey: string): boolean {
    const config = this.tenantConfigs?.find(c => c.key === attrKey);
    return config && config.value === TenantConfig.DISPLAY_AND_REQUIRED;
  }

  isHide(attrKey: string): boolean {
    const config = this.tenantConfigs?.find(c => c.key === attrKey);
    return config && config.value === TenantConfig.HIDE;
  }

  setSubjectAttrFormValue(model: ISubject): ISubject {
    model.owner = this.ownerForm.value;
    model.country = this.countryForm.value;
    model.state = this.stateForm.value;
    model.district = this.districtForm.value;
    model.site = this.siteForm.value;
    model.project = this.projectForm.value;

    return model;
  }

  onClickTab(tab: SelectedTabType) {
    if (!this.update) return;
    if (this.selectedTab === tab) return;

    const subject = this.subject;
    const SNChanged = this.serialNumberForm.value !== subject.serialNo || this.toggle.checked !== subject.isValid;

    // Form未変更時
    if (!SNChanged && !this.attrChanged) {
      this.selectedTab = tab;
      return;
    }

    // Form変更時
    const isError = this.isDisabledSubmit();
    const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, {
      width: "450px",
      height: "280px",
      disableClose: true,
      data: { isMove: true, isError },
    });
    dialogRef.afterClosed().subscribe((res) => {
      // cancel
      if (res === '') return;

      // save
      const keepDialog = true;
      if (res === true) this.submit(keepDialog);

      // don't save
      else if (res === false) this.resetForm();

      this.selectedTab = tab;
    });
  }

  private resetForm() {
    const subject = this.subject;

    this.serialNumberForm.reset(subject.serialNo.trim());
    this.toggle.checked = subject.isValid;

    this.attributesForm.reset({
      owner: subject.owner,
      country: subject.country,
      state: subject.state,
      district: subject.district,
      site: subject.site,
      project: subject.project
    });
  }
}
